import { useEffect as useEffectMsg, useRef as useRefMsg, useState as useStateMsg } from "react";
import { Api } from "./api";
import { Icon } from "./components";

// In-app messaging — community-scoped 1-on-1 threads. Inbox on the
// left, conversation on the right. Polling keeps the open thread
// fresh so users don't have to refresh.

const POLL_OPEN_MS = 5_000;    // active thread — user is actively reading
const POLL_INBOX_MS = 20_000;  // inbox — background

function _initialsM(name, email) {
  const src = (name || email || "").trim();
  if (!src) return "??";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function _relTimeMsg(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function _priceLabel(listing) {
  if (!listing) return "";
  if (listing.is_free) return "Free";
  if (listing.price_cents == null) return "Make offer";
  const dollars = listing.price_cents / 100;
  return "$" + dollars.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/* ─── AuthedThumb: small auth-protected image for marketplace covers ── */
// CODE_REVIEW P2 L266 — routes through the shared Api.authedBlobUrl
// cache. This is the worst offender of the five: the inbox polls every
// 20s and the thumb here is in the per-thread row, so without the cache
// every thread's cover image re-fetches on every poll.
function AuthedThumb({ imageId, size = 36, radius = 6 }) {
  const [src, setSrc] = useStateMsg(null);
  useEffectMsg(() => {
    if (!imageId) return;
    let cancelled = false;
    let release = null;
    window.Api.authedBlobUrl(`/api/marketplace/images/${imageId}`).then(
      ({ url, release: r }) => {
        if (cancelled) { r(); return; }
        release = r;
        setSrc(url);
      },
      () => { /* swallow — falls back to the placeholder icon below */ },
    );
    return () => { cancelled = true; if (release) release(); };
  }, [imageId]);
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, overflow: "hidden",
      background: "var(--bg-2)", display: "inline-flex", alignItems: "center", justifyContent: "center",
      color: "var(--mute)", flexShrink: 0,
    }}>
      {src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
           : <Icon name="image" size={size * 0.5} />}
    </div>
  );
}

/* ─── Top-level page ──────────────────────────────────────────────── */
function Messages({ user, openContext, onContextHandled }) {
  // openContext is set when navigation came from another route (e.g.
  // marketplace 'Message seller'). Shape:
  //   { thread_id }                          — open this thread
  //   { compose: { recipient_user_id, related_listing_id, listing_title } }
  //                                          — open the new-message flow
  const [threads, setThreads] = useStateMsg({ loading: true, error: null, items: [], meta: null });
  const [activeId, setActiveId] = useStateMsg(null);
  const [compose, setCompose] = useStateMsg(null);
  // Inbox pagination — CODE_REVIEW P2 L268. We deliberately keep the
  // current offset across poll refreshes so a user reading page 2
  // doesn't jump back to page 1 every 20s.
  const [offset, setOffset] = useStateMsg(0);
  // Request-invalidation counter — codex P1 on PR #156. Without this,
  // a user paging while a poll is in-flight could see the poll's
  // (older-offset) response overwrite their just-clicked page.
  const reqIdRef = useRefMsg(0);

  // If navigation handed us a context (from marketplace), honor it once.
  useEffectMsg(() => {
    if (!openContext) return;
    if (openContext.thread_id) setActiveId(openContext.thread_id);
    if (openContext.compose) setCompose(openContext.compose);
    onContextHandled && onContextHandled();
    // eslint-disable-next-line
  }, [openContext]);

  const reloadInbox = async (nextOffset = offset) => {
    const myReq = ++reqIdRef.current;
    try {
      const { items, meta } = await window.Api.messagesThreads({ offset: nextOffset });
      if (myReq !== reqIdRef.current) return;
      setThreads({ loading: false, error: null, items: items || [], meta: meta || null });
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      setThreads({ loading: false, error: err.message || "Couldn't load.", items: [], meta: null });
    }
  };

  useEffectMsg(() => {
    reloadInbox(offset);
    const i = setInterval(() => reloadInbox(offset), POLL_INBOX_MS);
    // Hide poll latency when the tab regains focus (most common case
    // on phones after the screen unlocks).
    const onVis = () => { if (document.visibilityState === "visible") reloadInbox(offset); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [offset]);

  const onSentNewThread = (thread) => {
    setCompose(null);
    setActiveId(thread.id);
    reloadInbox();
  };
  const onChangedThread = () => {
    reloadInbox();
  };

  // On mobile, only one pane is visible at a time. We swap based on
  // whether a thread or compose pane is "active". Desktop always shows
  // both panes side-by-side (CSS handles the responsive bit).
  const mobileShow = (activeId || compose) ? "thread" : "inbox";
  const backToInbox = () => { setActiveId(null); setCompose(null); };

  return (
    <div className="scroll" style={{ padding: 0 }}>
      <div className="messages-shell" data-mobile-show={mobileShow}>
        {/* Inbox column */}
        <div className="messages-inbox-col" style={{ borderRight: "1px solid var(--hairline)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="row" style={{ padding: "14px 16px", justifyContent: "space-between", borderBottom: "1px solid var(--hairline)" }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Messages</div>
            <span className="text-xs text-mute">
              {(() => {
                const total = threads.meta?.total ?? threads.items.length;
                return `${total} ${total === 1 ? "thread" : "threads"}`;
              })()}
            </span>
          </div>
          <div style={{ overflow: "auto", flex: 1 }}>
            {threads.loading && <div style={{ padding: 16, color: "var(--mute)", fontSize: 13 }}>Loading…</div>}
            {threads.error && <div style={{ padding: 16, color: "var(--danger)" }}>{threads.error}</div>}
            {!threads.loading && threads.items.length === 0 && !compose && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--mute)", fontSize: 13 }}>
                No messages yet. Start a conversation from the
                <span style={{ color: "var(--accent)" }}> Marketplace</span> by clicking
                "Message {`{seller}`}" on a listing.
              </div>
            )}
            {threads.items.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={activeId === t.id && !compose}
                onClick={() => { setCompose(null); setActiveId(t.id); }}
                onHide={(id) => {
                  // If the hidden thread was open, close the detail
                  // pane too — otherwise the right column shows a
                  // thread that's no longer in the inbox. Use a
                  // functional updater so a fast tap on a DIFFERENT
                  // thread between the DELETE call and its resolution
                  // doesn't close the NEW thread (codex P2 on
                  // PR #205): activeId in this closure can be stale.
                  setActiveId((cur) => (cur === id ? null : cur));
                  reloadInbox();
                }}
              />
            ))}
            <div style={{ padding: "0 12px" }}>
              <window.Pager meta={threads.meta} offset={offset} onChange={setOffset} busy={threads.loading} />
            </div>
          </div>
        </div>

        {/* Right pane: thread or compose or empty */}
        <div className="messages-detail-col" style={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          {compose ? (
            <ComposeNew
              user={user}
              context={compose}
              onCancel={() => setCompose(null)}
              onSent={onSentNewThread}
              onBack={backToInbox}
            />
          ) : activeId ? (
            <ThreadView
              key={activeId}
              threadId={activeId}
              user={user}
              onChanged={onChangedThread}
              onDeleted={() => { setActiveId(null); reloadInbox(); }}
              onBack={backToInbox}
            />
          ) : (
            <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", color: "var(--mute)", padding: 32, textAlign: "center" }}>
              <div>
                <Icon name="chat" size={40} style={{ opacity: 0.4, marginBottom: 12 }} />
                <div>Pick a conversation, or start one from the Marketplace.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Returns null if there's nothing extra to show beyond a normal accepted
// thread. Otherwise returns { text, tone } where tone is "info" | "warn"
// driving the badge color.
function _requestBadge(thread) {
  const status = thread.status;
  const role = thread.request_role;
  if (status === "accepted") return null;
  if (status === "pending" && role === "recipient") {
    return { text: "Message request", tone: "info" };
  }
  if (status === "pending" && role === "initiator") {
    return { text: "Pending", tone: "info" };
  }
  if (status === "denied") {
    return { text: "Declined", tone: "warn" };
  }
  return null;
}

function ThreadRow({ thread, active, onClick, onHide }) {
  const other = thread.other_participant;
  const name = (other.full_name || "").trim() || "Neighbor";
  const snippet = thread.last_message?.body || "(no messages yet)";
  const isMine = thread.last_message?.is_mine;
  const badge = _requestBadge(thread);
  // Click × → confirm modal (Mike's spec: "are you sure you wish to
  // delete?" with Yes / No). Pending = network request in flight after
  // the user picked Yes — we optimistic-hide the row at that point.
  const [confirming, setConfirming] = useStateMsg(false);
  const [pending, setPending] = useStateMsg(false);
  const askHide = (e) => {
    e.stopPropagation();
    setConfirming(true);
  };
  const cancelHide = (e) => {
    if (e) e.stopPropagation();
    setConfirming(false);
  };
  const confirmHide = (e) => {
    if (e) e.stopPropagation();
    if (pending) return;
    setPending(true);
    window.Api.messagesHideThread(thread.id)
      .then(() => { if (onHide) onHide(thread.id); })
      .catch(() => {
        setPending(false);
        setConfirming(false);
      });
  };
  if (pending) return null;  // optimistic — row vanishes pre-server-confirm
  return (
    <div
      className="msg-thread-row"
      style={{
        position: "relative",
        background: active ? "var(--bg-2)" : "transparent",
        borderBottom: "1px solid var(--hairline)",
      }}
    >
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "flex", alignItems: "flex-start", gap: 10, width: "100%",
          padding: "12px 38px 12px 14px",  /* right room for the X */
          textAlign: "left",
          background: "transparent",
          border: 0,
          cursor: "pointer",
        }}
      >
        <span className="avatar avatar--t" style={{ flexShrink: 0 }}>
          {_initialsM(other.full_name, name)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ justifyContent: "space-between", gap: 6 }}>
            <div style={{
              fontWeight: thread.unread ? 600 : 500,
              fontSize: 13.5,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {name}
            </div>
            <div className="text-xs text-mute" style={{ flexShrink: 0 }}>{_relTimeMsg(thread.last_message_at)}</div>
          </div>
          {badge && (
            <div style={{ marginTop: 4 }}>
              <span
                className="pill"
                style={{
                  fontSize: 10.5,
                  padding: "1px 8px",
                  background: badge.tone === "warn" ? "var(--danger-bg, #f5d6cc)" : "var(--bg-2)",
                  color: badge.tone === "warn" ? "var(--danger, #a33)" : "var(--ink)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 999,
                }}
              >
                {badge.text}
              </span>
            </div>
          )}
          <div className="text-xs" style={{
            color: thread.unread ? "var(--ink)" : "var(--mute)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            marginTop: 2,
          }}>
            {isMine ? "You: " : ""}{snippet}
          </div>
          {thread.related_listing && (
            <div className="row" style={{ gap: 6, marginTop: 6, alignItems: "center" }}>
              <AuthedThumb imageId={thread.related_listing.cover_image_id} size={20} radius={4} />
              <span className="text-xs text-mute" style={{
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {thread.related_listing.title}
              </span>
            </div>
          )}
        </div>
        {thread.unread && (
          <span style={{
            flexShrink: 0,
            width: 8, height: 8, borderRadius: "50%",
            background: "var(--accent)", marginTop: 6,
          }} />
        )}
      </button>
      <button
        type="button"
        className="msg-thread-row__hide"
        onClick={askHide}
        aria-label={`Delete conversation with ${name} from inbox`}
        title="Delete from inbox (a reply from them will bring it back)"
      >
        ×
      </button>
      {confirming && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`msg-confirm-title-${thread.id}`}
          /* stopPropagation on the backdrop so clicking outside the
             modal doesn't fall through to the row-open button. */
          onClick={(e) => { e.stopPropagation(); cancelHide(); }}
        >
          <div
            className="modal"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id={`msg-confirm-title-${thread.id}`}
              className="display"
              style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}
            >
              Delete this conversation?
            </h3>
            <p style={{ color: "var(--mute)", fontSize: 14, lineHeight: 1.5, margin: 0 }}>
              Are you sure you wish to delete your conversation with <b>{name}</b>?
              It will be removed from your inbox. If {name.split(" ")[0]} replies, it will come back.
            </p>
            <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="btn btn--ghost" onClick={cancelHide}>No</button>
              <button
                className="btn btn--primary"
                onClick={confirmHide}
                style={{ background: "var(--danger, #c0392b)", borderColor: "var(--danger, #c0392b)" }}
                autoFocus
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── One conversation ────────────────────────────────────────────── */
function ThreadView({ threadId, user, onChanged, onBack }) {
  const [thread, setThread] = useStateMsg(null);
  const [error, setError] = useStateMsg(null);
  const [draft, setDraft] = useStateMsg("");
  const [sending, setSending] = useStateMsg(false);
  const [deciding, setDeciding] = useStateMsg(false);
  const scrollRef = useRefMsg(null);
  const lastSeenIdRef = useRefMsg(0);

  const load = async (silent = false) => {
    try {
      const t = await window.Api.messagesThread(threadId);
      setThread(t);
      // Mark read on load (and on any poll that brings new messages).
      const newest = t.messages.length > 0 ? t.messages[t.messages.length - 1].id : 0;
      if (newest > lastSeenIdRef.current) {
        lastSeenIdRef.current = newest;
        try { await window.Api.messagesMarkRead(threadId); } catch (_) {}
        onChanged && onChanged();
      }
    } catch (err) {
      if (!silent) setError(err.message || "Couldn't load thread.");
    }
  };

  useEffectMsg(() => {
    lastSeenIdRef.current = 0;
    setThread(null);
    setError(null);
    setDraft("");
    load();
    const i = setInterval(() => load(true), POLL_OPEN_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [threadId]);

  // Auto-scroll to bottom on new messages
  useEffectMsg(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages?.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const msg = await window.Api.messagesSend(threadId, body);
      setDraft("");
      // Dedupe by id: a background poll can land between the POST
      // committing server-side and this handler resolving, in which
      // case `cur.messages` already contains `msg` and a naive append
      // briefly renders a duplicate bubble until the next poll
      // (codex/CODE_REVIEW #273).
      setThread((cur) => {
        if (!cur) return cur;
        if (cur.messages.some((m) => m.id === msg.id)) return cur;
        return { ...cur, messages: [...cur.messages, msg], last_message_at: msg.created_at };
      });
      onChanged && onChanged();
    } catch (err) {
      alert(err.message || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  const decideRequest = async (action) => {
    if (deciding) return;
    setDeciding(true);
    try {
      const fn = action === "accept" ? window.Api.messagesAcceptThread : window.Api.messagesDenyThread;
      const updated = await fn(threadId);
      setThread(updated);
      onChanged && onChanged();
    } catch (err) {
      alert(err.message || `Couldn't ${action} the request.`);
    } finally {
      setDeciding(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Even before the thread loads (or if it errors), give the user a
  // mobile-visible back button so they're never stuck on a blank
  // pane after the inbox slid out of view.
  const _backRow = (
    <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--hairline)" }}>
      <button
        type="button"
        className="messages-back-btn"
        onClick={onBack}
        aria-label="Back to inbox"
      >
        <Icon name="chevron" size={16} style={{ transform: "rotate(180deg)" }} />
        &nbsp;Back
      </button>
    </div>
  );
  if (error) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {_backRow}
        <div style={{ padding: 24, color: "var(--danger)" }}>{error}</div>
      </div>
    );
  }
  if (!thread) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {_backRow}
        <div style={{ padding: 24, color: "var(--mute)" }}>Loading…</div>
      </div>
    );
  }

  const other = thread.other_participant;
  const otherName = (other.full_name || "").trim() || "Neighbor";

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--hairline)" }}>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <button
            type="button"
            className="messages-back-btn"
            onClick={onBack}
            aria-label="Back to inbox"
          >
            <Icon name="chevron" size={16} style={{ transform: "rotate(180deg)" }} />
          </button>
          <span className="avatar avatar--t">{_initialsM(other.full_name, otherName)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{otherName}</div>
            <div className="text-xs text-mute">Neighbor in your community</div>
          </div>
        </div>
        {thread.related_listing && (
          <div className="row" style={{
            marginTop: 12, padding: "8px 12px", borderRadius: 10,
            background: "var(--bg-2)",
            gap: 10, alignItems: "center",
          }}>
            <AuthedThumb imageId={thread.related_listing.cover_image_id} size={40} radius={6} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="text-xs text-mute" style={{ marginBottom: 2 }}>Conversation about</div>
              <div style={{ fontWeight: 500, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {thread.related_listing.title}
              </div>
            </div>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{_priceLabel(thread.related_listing)}</span>
            {thread.related_listing.status !== "active" && (
              <span className="pill pill--ghost" style={{ flexShrink: 0 }}>
                {thread.related_listing.status === "sold" ? "Sold" : "Closed"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Request bar — visible only when thread isn't an open conversation. */}
      <RequestBar
        thread={thread}
        otherName={otherName}
        onAccept={() => decideRequest("accept")}
        onDeny={() => decideRequest("deny")}
        busy={deciding}
      />

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: 18, background: "var(--bg)" }}>
        {thread.messages.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--mute)", marginTop: 40, fontSize: 13 }}>
            No messages yet. Start the conversation below.
          </div>
        ) : thread.messages.map((m) => (
          <Bubble key={m.id} message={m} />
        ))}
      </div>

      {/* Composer — only when the conversation is actually open. While
          PENDING/DENIED the request bar above is the only interaction. */}
      {thread.status === "accepted" && (
        <div style={{
          padding: 14,
          // iOS home-indicator safe area on iPhone X+ (codex P2 on #250).
          paddingBottom: "calc(14px + env(safe-area-inset-bottom))",
          borderTop: "1px solid var(--hairline)",
          background: "var(--surface)",
        }}>
          <div className="row" style={{ gap: 10, alignItems: "flex-end" }}>
            <textarea
              className="input"
              // aria-label: placeholders aren't labels (VoiceOver/NVDA
              // announce "edit text, blank" once a user starts typing).
              // Codex P3 on #277.
              aria-label="Type your message"
              placeholder={`Message ${otherName}…`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              rows={2}
              style={{ resize: "none", flex: 1, lineHeight: 1.4, padding: "10px 12px" }}
            />
            <button
              className="btn btn--primary"
              onClick={send}
              disabled={sending || !draft.trim()}
              style={{ padding: "10px 16px" }}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
          <div className="text-xs text-mute" style={{ marginTop: 6 }}>
            Press Enter to send, Shift+Enter for a new line.
          </div>
        </div>
      )}
    </div>
  );
}

function RequestBar({ thread, otherName, onAccept, onDeny, busy }) {
  const status = thread.status;
  const role = thread.request_role;
  if (status === "accepted") return null;

  // Recipient sees the accept/deny controls (or just the Accept button on a
  // previously-denied thread they want to reopen).
  if (role === "recipient" && status === "pending") {
    return (
      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--hairline)",
        background: "var(--bg-2)",
      }}>
        <div style={{ fontSize: 14, marginBottom: 10 }}>
          <b>Message request from {otherName}.</b>
          <span className="text-mute"> Accept to start the conversation, or decline.</span>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn"
            onClick={onAccept}
            disabled={busy}
            style={{
              background: "#1f8a4a", color: "white", border: 0,
              padding: "8px 14px", fontWeight: 600,
            }}
          >
            {busy ? "…" : "Accept"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onDeny}
            disabled={busy}
            style={{
              background: "#c4422f", color: "white", border: 0,
              padding: "8px 14px", fontWeight: 600,
            }}
          >
            {busy ? "…" : "Decline"}
          </button>
        </div>
      </div>
    );
  }
  if (role === "recipient" && status === "denied") {
    return (
      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--hairline)",
        background: "var(--bg-2)",
      }}>
        <div style={{ fontSize: 14, marginBottom: 10 }}>
          You declined this request from {otherName}.
          <span className="text-mute"> Change your mind?</span>
        </div>
        <button
          type="button"
          className="btn"
          onClick={onAccept}
          disabled={busy}
          style={{
            background: "#1f8a4a", color: "white", border: 0,
            padding: "8px 14px", fontWeight: 600,
          }}
        >
          {busy ? "…" : "Accept now"}
        </button>
      </div>
    );
  }
  if (role === "initiator" && status === "pending") {
    return (
      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--hairline)",
        background: "var(--bg-2)",
        fontSize: 14, color: "var(--mute)",
      }}>
        Waiting for {otherName} to accept your message request.
      </div>
    );
  }
  if (role === "initiator" && status === "denied") {
    return (
      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--hairline)",
        background: "var(--bg-2)",
        fontSize: 14, color: "var(--mute)",
      }}>
        {otherName} declined your request. You can send a new one from the directory.
      </div>
    );
  }
  return null;
}

function Bubble({ message }) {
  const mine = message.is_mine;
  return (
    <div style={{
      display: "flex",
      justifyContent: mine ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      <div style={{
        maxWidth: "70%",
        background: mine ? "var(--accent)" : "var(--surface)",
        color: mine ? "var(--surface)" : "var(--ink)",
        border: mine ? "0" : "1px solid var(--hairline)",
        borderRadius: 12,
        padding: "8px 12px",
        whiteSpace: "pre-wrap",
        // `word-break: break-word` is the legacy non-standard form;
        // iOS Safari and a few other engines treat it inconsistently
        // so a long URL or any other unbreakable token in a message
        // body would slip past the bubble's 70% cap and push the
        // viewport horizontally on mobile. `overflow-wrap: anywhere`
        // is the standards-compliant equivalent that breaks inside
        // arbitrary characters when there's no other valid wrap
        // point — exactly what we want for chat content.
        overflowWrap: "anywhere",
        fontSize: 14,
        lineHeight: 1.45,
      }}>
        {message.body}
        <div style={{
          fontSize: 10, marginTop: 4,
          opacity: 0.65, textAlign: "right",
        }}>
          {_relTimeMsg(message.created_at)}
        </div>
      </div>
    </div>
  );
}

/* ─── New thread (entered from outside the messages page) ────────── */
function ComposeNew({ user, context, onCancel, onSent, onBack }) {
  // Seed the body with a context-appropriate opener. Caller can attach
  // a listing, a service offer, or an assistance request — exactly one.
  const _initialBody = (() => {
    if (!context.listing_title) return "";
    if (context.related_listing_id) return `Hi! I'm interested in your listing "${context.listing_title}".`;
    if (context.related_service_id) return `Hi! I'm interested in your service "${context.listing_title}".`;
    if (context.related_assistance_id) return `Hi! I saw your request "${context.listing_title}" and I'd like to help.`;
    return "";
  })();
  const [body, setBody] = useStateMsg(_initialBody);
  const [busy, setBusy] = useStateMsg(false);
  const [err, setErr] = useStateMsg(null);

  const send = async () => {
    if (busy || !body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const thread = await window.Api.messagesCreateThread({
        recipient_user_id: context.recipient_user_id,
        related_listing_id: context.related_listing_id,
        related_service_id: context.related_service_id,
        related_assistance_id: context.related_assistance_id,
        body: body.trim(),
      });
      onSent(thread);
    } catch (e) {
      setErr(e.message || "Couldn't send.");
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--hairline)" }}>
        <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
          <div className="row" style={{ gap: 6, alignItems: "center", minWidth: 0 }}>
            <button
              type="button"
              className="messages-back-btn"
              onClick={onBack || onCancel}
              aria-label="Back to inbox"
            >
              <Icon name="chevron" size={16} style={{ transform: "rotate(180deg)" }} />
            </button>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              New message{context.recipient_name ? ` to ${context.recipient_name}` : ""}
            </div>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>
        {context.listing_title && (
          <div className="text-sm text-mute" style={{ marginTop: 6 }}>
            {context.related_service_id ? "About service" : context.related_assistance_id ? "About request" : "About listing"}: <b>{context.listing_title}</b>
          </div>
        )}
      </div>
      <div style={{ padding: 18, flex: 1, display: "flex", flexDirection: "column" }}>
        {err && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", marginBottom: 10 }}>
            {err}
          </div>
        )}
        <textarea
          className="input"
          rows={8}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Say hi…"
          style={{ resize: "vertical", lineHeight: 1.5 }}
          autoFocus
        />
        <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn--primary" onClick={send} disabled={busy || !body.trim()}>
            {busy ? "Sending…" : "Send message"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { Messages };
